import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { SlideToConfirmButton } from '@components/SlideToConfirmButton';
import { useOrders } from '@hooks/useOrders';
import type { Order } from '@services/orderTypes';
import type { Asset } from '@shared/types';
import { formatOptionPrice } from '@utils/price-formatting';

interface ManageOrdersPanelProps {
  isVisible: boolean;
  orders: Order[];
  strikePrice: number;
  asset: Asset;
  onClose: () => void;
}

export function ManageOrdersPanel({
  isVisible,
  orders,
  strikePrice,
  asset,
  onClose,
}: ManageOrdersPanelProps) {
  const { cancelOrder } = useOrders();
  const [showCancelAll, setShowCancelAll] = useState(false);
  const prevVisibleRef = useRef(isVisible);

  const handleCancelAll = () => {
    // Cancel all orders
    orders.forEach(order => {
      cancelOrder(order.id);
    });
    // Close the panel after canceling all
    onClose();
  };

  // Reset showCancelAll when panel transitions from hidden to visible
  useEffect(() => {
    const wasHidden = !prevVisibleRef.current;
    const isNowVisible = isVisible;

    if (wasHidden && isNowVisible) {
      // Panel just opened - reset to default state
      setShowCancelAll(false);
    }

    prevVisibleRef.current = isVisible;
  }, [isVisible]);

  if (!isVisible || orders.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="flex-shrink-0 bg-brand-dark/95 backdrop-blur-sm border-t-2 border-divider overflow-hidden"
      style={{
        maxHeight: '40vh',
      }}
    >
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex gap-4 items-center">
          <h3 className="text-white text-lg font-bold">Open Orders</h3>
          <p className="text-white/70 text-base">
            {asset}{' '}
            <span className="text-white">
              $
              {strikePrice.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            {' Strike'}
          </p>
        </div>

        {/* Orders List */}
        <div className="space-y-2 max-h-[30vh] overflow-y-auto">
          {orders.map(order => (
            <div
              key={order.id}
              className="bg-surface border border-border-subtle rounded-lg pl-3 pr-8 py-2"
            >
              <div className="grid grid-cols-4 gap-3 items-center text-sm">
                {/* Type */}
                <div className="flex items-center gap-2">
                  <span
                    className={`font-bold text-lg ${
                      order.type === 'CALL' ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {order.type === 'CALL' ? '↑' : '↓'}
                  </span>
                  <span className="text-label text-xs">{order.type}</span>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-2">
                  <span className="text-label text-xs">QTY</span>
                  <span className="text-white font-mono">{order.quantity}</span>
                </div>

                {/* Price */}
                <div className="flex items-center gap-2">
                  <span className="text-label text-xs">PRICE</span>
                  <span className="text-white font-mono">
                    {order.orderType === 'LIMIT' && order.limitPrice
                      ? `$${formatOptionPrice(order.limitPrice)}`
                      : 'Market'}
                  </span>
                </div>

                {/* Total */}
                <div className="flex items-center gap-2">
                  <span className="text-label text-xs">TOTAL</span>
                  <span className="text-white font-mono">
                    {order.orderType === 'LIMIT' && order.limitPrice
                      ? `$${(order.quantity * order.limitPrice).toFixed(2)}`
                      : 'Market'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Actions */}
        {showCancelAll ? (
          <SlideToConfirmButton
            onConfirm={handleCancelAll}
            confirmText="RELEASE TO CANCEL"
            className="w-full h-12 bg-red-500/80"
          >
            SLIDE TO CANCEL ALL ORDERS
          </SlideToConfirmButton>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setShowCancelAll(true)}
              className="flex-1 h-12 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-semibold transition-colors"
            >
              Cancel All Orders
            </button>
            <button
              onClick={onClose}
              className="flex-1 h-12 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Hide
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
